import React, { useState, useMemo, useEffect, useRef } from "react";
import { CaptionStyles } from "../../../types";
import { ToggleGroup, ToggleGroupItem } from "../../ui/toggle-group";
import { AlignLeft, AlignCenter, AlignRight, RotateCcw, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Slider } from "../../ui/slider";
import { GOOGLE_FONTS_LIST } from "../../../data/google-fonts-list";
import { useFontPreviewLoader } from "../../../utils/text/load-font-preview";
import { Input } from "../../ui/input";
import type { FontInfo } from "@remotion/google-fonts";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

/**
 * Font weight options
 */
const fontWeights = [
  { value: "100", label: "Thin" },
  { value: "200", label: "Extra Light" },
  { value: "300", label: "Light" },
  { value: "400", label: "Regular" },
  { value: "500", label: "Medium" },
  { value: "600", label: "Semi Bold" },
  { value: "700", label: "Bold" },
  { value: "800", label: "Extra Bold" },
  { value: "900", label: "Black" },
];

/**
 * Extract available weights from FontInfo metadata
 */
function extractAvailableWeights(fontInfo: FontInfo): string[] {
  const weights = new Set<string>();

  if (fontInfo.fonts.normal) {
    Object.keys(fontInfo.fonts.normal).forEach((weight) => weights.add(weight));
  }

  if (fontInfo.fonts.italic) {
    Object.keys(fontInfo.fonts.italic).forEach((weight) => weights.add(weight));
  }

  return Array.from(weights).sort((a, b) => parseInt(a) - parseInt(b));
}

interface FontItemProps {
  fontFamily: string;
  previewUrl: string;
  isSelected: boolean;
  onClick: () => void;
  loadFontForPreview: (fontFamily: string, previewUrl?: string) => Promise<void>;
  isFontLoaded: (fontFamily: string) => boolean;
  makeFontPreviewName: (fontFamily: string) => string;
}

const FontItem: React.FC<FontItemProps> = ({
  fontFamily,
  previewUrl,
  isSelected,
  onClick,
  loadFontForPreview,
  isFontLoaded,
  makeFontPreviewName,
}) => {
  const itemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const element = itemRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isFontLoaded(fontFamily)) {
            loadFontForPreview(fontFamily, previewUrl).catch(() => {});
          }
        });
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [fontFamily, previewUrl, loadFontForPreview, isFontLoaded]);

  const fontLoaded = isFontLoaded(fontFamily);
  const fontFamilyStyle = fontLoaded
    ? { fontFamily: makeFontPreviewName(fontFamily) }
    : {};

  return (
    <button
      ref={itemRef}
      onClick={onClick}
      className={`w-full px-3 py-2 text-xs text-left hover:bg-accent hover:text-accent-foreground ${
        isSelected ? "bg-accent text-accent-foreground" : ""
      }`}
      style={fontFamilyStyle}
    >
      {fontFamily}
    </button>
  );
};

interface CaptionTypographySettingsProps {
  styles: CaptionStyles;
  onStyleChange: (field: keyof CaptionStyles, value: any) => void;
}

/**
 * CaptionTypographySettings Component
 *
 * Provides controls for typography settings:
 * - Font Family (searchable dropdown)
 * - Font Size (slider)
 * - Font Weight (select dropdown)
 * - Letter Spacing (slider)
 * - Text Alignment (toggle group)
 */
export const CaptionTypographySettings: React.FC<CaptionTypographySettingsProps> = ({
  styles,
  onStyleChange,
}) => {
  const [fontSearch, setFontSearch] = useState("");
  const [availableWeights, setAvailableWeights] = useState<string[]>(["400"]);
  const { loadFontForPreview, makeFontPreviewName, isFontLoaded } =
    useFontPreviewLoader();

  // Update available weights when font family changes
  useEffect(() => {
    if (!styles.fontFamily) {
      setAvailableWeights(["400"]);
      return;
    }

    const fetchFontWeights = async () => {
      try {
        // Extract the font name without fallbacks
        const fontName = styles.fontFamily.split(",")[0].trim();
        const response = await fetch(`/api/fonts/${fontName}`);

        if (!response.ok) {
          setAvailableWeights(["400"]);
          return;
        }

        const fontInfo: FontInfo = await response.json();
        const weights = extractAvailableWeights(fontInfo);
        setAvailableWeights(weights);

        // Reset to default weight if current weight is not available
        const currentWeight = String(styles.fontWeight || "400");
        if (!weights.includes(currentWeight)) {
          const defaultWeight = weights.includes("400") ? "400" : weights[0];
          onStyleChange("fontWeight", parseInt(defaultWeight));
        }
      } catch {
        setAvailableWeights(["400"]);
      }
    };

    fetchFontWeights();
  }, [styles.fontFamily]);

  // Filter fonts based on search
  const filteredFonts = useMemo(() => {
    if (!fontSearch) return GOOGLE_FONTS_LIST;
    const searchTerm = fontSearch.toLowerCase();
    return GOOGLE_FONTS_LIST.filter((font) =>
      font.fontFamily.toLowerCase().includes(searchTerm)
    );
  }, [fontSearch]);

  // Parse current font size to rem value
  const currentFontSizeRem = useMemo(() => {
    const fs = styles.fontSize;
    if (!fs) return 2;
    const numeric = parseFloat(fs);
    if (Number.isNaN(numeric)) return 2;
    return numeric;
  }, [styles.fontSize]);

  // Parse current letter spacing to em value
  const currentLetterSpacingEm = useMemo(() => {
    const ls = styles.letterSpacing;
    if (!ls) return 0;
    const numeric = parseFloat(ls);
    if (Number.isNaN(numeric)) return 0;
    if (ls.endsWith("px")) return numeric / 16;
    return numeric;
  }, [styles.letterSpacing]);

  const showFontSizeReset = currentFontSizeRem !== 2;
  const showLetterSpacingReset = currentLetterSpacingEm !== 0;

  return (
    <TooltipProvider>
      <div className="space-y-4 rounded-md bg-sidebar p-2.5 border min-w-0 w-full max-w-full">
        <h5 className="text-sm font-light leading-none text-foreground">
          Typography
        </h5>

        <div className="grid grid-cols-2 gap-2">
          {/* Font Family */}
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground/50">Font Family</label>
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full px-3 py-2 text-xs text-left text-foreground bg-background border rounded-md hover:bg-accent hover:text-accent-foreground truncate">
                  {styles.fontFamily?.split(",")[0] || "Select a font"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" side="bottom" align="start">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      placeholder="Search fonts..."
                      value={fontSearch}
                      onChange={(e) => setFontSearch(e.target.value)}
                      className="pl-7 h-8 text-xs"
                    />
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {filteredFonts.length === 0 ? (
                    <div className="p-4 text-xs text-center text-muted-foreground">
                      No fonts found
                    </div>
                  ) : (
                    filteredFonts.map((font) => (
                      <FontItem
                        key={font.importName}
                        fontFamily={font.fontFamily}
                        previewUrl={font.previewUrl}
                        isSelected={styles.fontFamily?.includes(font.fontFamily)}
                        onClick={() => {
                          onStyleChange("fontFamily", `${font.fontFamily}, sans-serif`);
                          setFontSearch("");
                        }}
                        loadFontForPreview={loadFontForPreview}
                        isFontLoaded={isFontLoaded}
                        makeFontPreviewName={makeFontPreviewName}
                      />
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Font Weight */}
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground/50">Font Weight</label>
            <Select
              value={String(styles.fontWeight || "400")}
              onValueChange={(value) => onStyleChange("fontWeight", parseInt(value))}
            >
              <SelectTrigger className="w-full text-xs text-foreground">
                <SelectValue placeholder="Select weight" />
              </SelectTrigger>
              <SelectContent>
                {fontWeights
                  .filter((weight) => availableWeights.includes(weight.value))
                  .map((weight) => (
                    <SelectItem
                      key={weight.value}
                      value={weight.value}
                      className="text-xs text-foreground"
                      style={{ fontWeight: weight.value }}
                    >
                      {weight.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Font Size */}
        <div className="space-y-2 min-w-0 w-full">
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground/50">Font Size</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {currentFontSizeRem.toFixed(1)}rem
              </span>
              {showFontSizeReset && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onStyleChange("fontSize", "2rem")}
                      className="text-xs px-1.5 py-1 rounded-md transition-colors bg-secondary hover:bg-secondary/80 text-secondary-foreground"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Reset font size</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="w-full min-w-0">
            <Slider
              value={[currentFontSizeRem]}
              onValueChange={(value) => onStyleChange("fontSize", `${value[0]}rem`)}
              min={1}
              max={4}
              step={0.1}
              className="w-full"
            />
          </div>
        </div>

        {/* Letter Spacing */}
        <div className="space-y-2 min-w-0 w-full">
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground/50">Letter Spacing</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {currentLetterSpacingEm.toFixed(2)}em
              </span>
              {showLetterSpacingReset && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onStyleChange("letterSpacing", "0em")}
                      className="text-xs px-1.5 py-1 rounded-md transition-colors bg-secondary hover:bg-secondary/80 text-secondary-foreground"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Reset letter spacing</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="w-full min-w-0">
            <Slider
              value={[currentLetterSpacingEm]}
              onValueChange={(value) =>
                onStyleChange("letterSpacing", `${value[0].toFixed(2)}em`)
              }
              min={-0.1}
              max={0.5}
              step={0.01}
              className="w-full"
            />
          </div>
        </div>

        {/* Text Alignment */}
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground/50">Alignment</label>
          <ToggleGroup
            type="single"
            size="sm"
            className="justify-start gap-1 text-foreground"
            value={styles.textAlign || "center"}
            onValueChange={(value) => {
              if (value) onStyleChange("textAlign", value);
            }}
          >
            <ToggleGroupItem value="left" aria-label="Align left" className="h-9 w-9">
              <AlignLeft className="h-4 w-4 text-foreground" />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="center"
              aria-label="Align center"
              className="h-9 w-9"
            >
              <AlignCenter className="h-4 w-4 text-foreground" />
            </ToggleGroupItem>
            <ToggleGroupItem value="right" aria-label="Align right" className="h-9 w-9">
              <AlignRight className="h-4 w-4 text-foreground" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
    </TooltipProvider>
  );
};
